'use client';

import type { FC } from 'react';
import Eyebrow from '@/components/ui/Eyebrow';
import Form from '@/components/ui/Form';
import Input from '@/components/ui/Input';
import SubmitButton from '@/components/ui/SubmitButton';
import Textarea from '@/components/ui/Textarea';
import useFeatureLeadForm from '@/lib/hooks/useFeatureLeadForm';
import cn from '@/lib/packages/cn';
import { helperTextVariants, inputVariants } from '@/lib/packages/tailwind.variants';

type FeatureLeadFormProps = {
  feature: string;
  heading?: string;
  supportingText?: string;
  submitLabel?: string;
  className?: string;
  showBadge?: boolean;
};

const FeatureLeadForm: FC<FeatureLeadFormProps> = ({
  feature,
  heading = 'See it in action',
  supportingText = 'Tell us a bit about your goal and we’ll tailor a live session for you.',
  submitLabel = 'Request a walkthrough',
  className,
  showBadge = true,
}) => {
  const { form, handleSubmit } = useFeatureLeadForm(feature);
  const rolesError = form.formState.errors.roles as { message?: string } | undefined;

  return (
    <div
      className={cn(
        'rounded-2xl border border-gray-200 bg-white/95 p-8 shadow-[0_35px_80px_rgba(15,23,42,0.18)] ring-1 ring-black/5 backdrop-blur dark:border-white/10 dark:bg-gray-950/80 dark:ring-white/5',
        className,
      )}
    >
      <div className='space-y-2'>
        {showBadge ? <Eyebrow>Feature form</Eyebrow> : null}
        <h3 className='text-2xl font-semibold text-gray-900 dark:text-white'>{heading}</h3>
        <p className='text-sm text-gray-600 dark:text-white/70'>{supportingText}</p>
      </div>
      <Form form={form} onSubmit={handleSubmit} className='mt-6 space-y-6'>
        <div className='grid gap-4 sm:grid-cols-2'>
          <Input name='firstName' label='First name' type='text' autoComplete='given-name' />
          <Input name='lastName' label='Last name' type='text' autoComplete='family-name' />
        </div>
        <Input name='company' label='Company' type='text' autoComplete='organization' />
        <fieldset className='w-full'>
          <legend className='mb-2 block text-sm font-medium text-secondary'>Role</legend>
          <div
            className={cn(
              inputVariants({ variant: rolesError ? 'error' : 'default' }),
              'flex w-full flex-wrap items-center gap-4 bg-white py-2 text-sm text-secondary dark:bg-gray-900 dark:text-white',
            )}
          >
            {['Broker', 'Trader', 'Physical Supplier'].map((role) => (
              <label key={role} className='inline-flex items-center gap-2'>
                <input
                  type='checkbox'
                  value={role}
                  defaultChecked={form.getValues('roles')?.includes(role)}
                  className='relative h-5 w-5 cursor-pointer appearance-none rounded border border-gray-300 bg-white align-middle text-white focus:outline-none focus:ring-2 focus:ring-info checked:border-info checked:bg-info before:pointer-events-none before:absolute before:inset-0 before:flex before:items-center before:justify-center before:text-base before:font-semibold before:text-transparent before:content-["✓"] checked:before:text-white dark:border-white/20 dark:bg-gray-900'
                  {...form.register('roles')}
                  aria-invalid={rolesError ? 'true' : 'false'}
                />
                <span>{role}</span>
              </label>
            ))}
          </div>
          {rolesError?.message ? (
            <p className={helperTextVariants({ variant: 'error', size: 'md' })}>{rolesError.message}</p>
          ) : null}
        </fieldset>
        <Input name='mobile' label='Mobile' type='tel' autoComplete='tel' />
        <Input name='email' label='Email' type='email' autoComplete='email' />
        <Textarea name='message' label='How can we help?' rows={5} size='md' autoComplete='off' />
        <div className='flex justify-end'>
          <SubmitButton className='min-w-[200px] justify-center'>{submitLabel}</SubmitButton>
        </div>
      </Form>
    </div>
  );
};

export default FeatureLeadForm;
